import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "../Images/Konn3ct Images/konn3ct_logo.png";
import { useCountry } from "../Context/CountryContext";
import { COUNTRY_CONFIG } from "../Geo/countryConfig";

const Konn3ctHeader = () => {
  const navigate = useNavigate();
  const { country, selectCountry } = useCountry();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);

  const featuresPath = `${country.route}/features`;
  const customersPath = `${country.route}/customers`;
  const pricingPath = `${country.route}/pricing`;
  const eventsPath = `${country.route}/events`;
  const aboutPath = `${country.route}/about`;

  const changeCountry = (code) => {
    const selected = selectCountry(code);
    setCountryOpen(false);
    setMobileOpen(false);

    // Preserve the current country-scoped page while switching country.
    // Example: /ng/pricing -> /gh/pricing.
    const currentPrefix = country.route;
    const suffix = location.pathname.startsWith(`${currentPrefix}/`)
      ? location.pathname.slice(currentPrefix.length)
      : "";

    navigate(`${selected.route}${suffix}`);
  };

  const nav = [
    { label: "Features", to: featuresPath },
    { label: "Customers", to: customersPath },
    { label: "Pricing", to: pricingPath },
    { label: "Events", to: eventsPath },
    { label: "About", to: aboutPath },
  ];

  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const companyDropdownRef = useRef(null);

  // Close mobile menu when clicking a link
  const closeMobileMenu = () => setMenuOpen(false);

  // ---------- ACTIVE HELPERS ----------
  const isActive = (path) => location.pathname === path;

  const navItemClass = (path) =>
    `relative text-sm font-medium transition
     hover:text-[#1A4699]
     ${isActive(path) ? "text-[#1A4699] font-semibold" : "text-gray-800"}`;

  const activeUnderline = "after:absolute after:left-0 after:-bottom-1 after:w-full after:h-[2px] after:bg-[#1A4699]";

  const isCompanyActive = [
    aboutPath,
    // "/BlogPage",
    // "/Konn3ctDevs",
    // "/Gallery",
  ].includes(location.pathname);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    // Listen to clicks on the entire document
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex justify-between items-center px-6 py-4 max-w-7xl mx-auto h-16">
        {/* LOGO */}
        <Link to={country.route} onClick={closeMobileMenu}>
          <img src={Logo} alt="Konn3ct Logo" className="h-8 w-auto cursor-pointer" />
        </Link>

        {/* DESKTOP MENU */}
        <nav className="hidden md:flex space-x-6 text-sm font-medium items-center">
          <Link to="/Meet" className={`${navItemClass("/Meet")} ${isActive("/Meet") ? activeUnderline : ""}`}>
            Meet
          </Link>
          <Link
            to={featuresPath}
            className={`${navItemClass(featuresPath)} ${isActive(featuresPath) ? activeUnderline : ""}`}
          >
            Features
          </Link>
          <Link
            to={customersPath}
            className={`${navItemClass(customersPath)} ${isActive(customersPath) ? activeUnderline : ""}`}
          >
            Customers
          </Link>
          <Link
            to={pricingPath}
            className={`${navItemClass(pricingPath)} ${isActive(pricingPath) ? activeUnderline : ""}`}
          >
            Pricing
          </Link>

          <Link
            to={aboutPath}
            className={`${navItemClass(aboutPath)} ${isActive(aboutPath) ? activeUnderline : ""}`}
          >
            About Us
          </Link>

          <Link
            to={eventsPath}
            className={`${navItemClass(eventsPath)} ${isActive(eventsPath) ? activeUnderline : ""}`}
          >
            Events
          </Link>
        </nav>

        {/* DESKTOP AUTH BUTTONS */}

        <div className="hidden md:flex space-x-3">
          <Link to="https://dashboard.konn3ct.ng/join" className="px-4 py-2 text-sm text-[#1A4699] hover:text-blue-500">
            Join a Meeting Room
          </Link>

          <button
            onClick={() =>
              (window.location.href =
                "https://www.newwavesecosystem.com/appointment/6?staff_user_id=32&invite_token=96ed5857091e47bdbded299526f2d5de&filter_appointment_type_ids=%5B6%5D")
            }
            className="px-4 py-2 text-sm text-white bg-blue-500 rounded-lg hover:bg-[#1A4699] transition shadow-sm"
          >
            Book a session
          </button>

          <button
            onClick={() => (window.location.href = "https://dashboard.konn3ct.ng/login")}
            className="px-4 py-2 text-sm bg-transparent border border-[#1A4699] rounded-lg text-black hover:text-blue-500 transition shadow-sm"
          >
            Login
          </button>
          <button
            onClick={() => (window.location.href = "https://dashboard.konn3ct.ng/register")}
            className="px-4 py-2 text-sm text-white bg-[#1A4699] rounded-lg hover:bg-blue-500 transition shadow-sm"
          >
            Sign up, It’s Free
          </button>
        </div>

        {/* MOBILE HAMBURGER */}
        <button
          className="md:hidden focus:outline-none text-gray-800"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={28} /> : <Menu size={26} />}
        </button>
      </div>

      {/* MOBILE MENU - Also uses click for Company */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg px-6 py-6 space-y-4">
          <Link
            to="/Meet"
            onClick={closeMobileMenu}
            //   className="block text-lg font-medium text-gray-800 hover:text-[#1A4699]"
            // >

            className={`block text-lg font-medium transition
                ${isActive("/Meet") ? "text-[#1A4699]" : "text-gray-800 hover:text-[#1A4699]"}`}
          >
            Meet
          </Link>
          <Link
            to={featuresPath}
            onClick={closeMobileMenu}
            className={`block text-lg font-medium transition
                ${isActive(featuresPath) ? "text-[#1A4699]" : "text-gray-800 hover:text-[#1A4699]"}`}
          >
            Features
          </Link>
          <Link
            to={customersPath}
            onClick={closeMobileMenu}
            className={`block text-lg font-medium transition
                ${isActive(customersPath) ? "text-[#1A4699]" : "text-gray-800 hover:text-[#1A4699]"}`}
          >
            Customers
          </Link>
          <Link
            to={pricingPath}
            onClick={closeMobileMenu}
            className={`block text-lg font-medium transition
                ${isActive(pricingPath) ? "text-[#1A4699]" : "text-gray-800 hover:text-[#1A4699]"}`}
          >
            Pricing
          </Link>
          <Link
            to={eventsPath}
            onClick={closeMobileMenu}
            className={`block text-lg font-medium transition
                ${isActive(eventsPath) ? "text-[#1A4699]" : "text-gray-800 hover:text-[#1A4699]"}`}
          >
            About Us
          </Link>
          <Link
            to={eventsPath}
            onClick={closeMobileMenu}
            className={`block text-lg font-medium transition
                ${isActive(eventsPath) ? "text-[#1A4699]" : "text-gray-800 hover:text-[#1A4699]"}`}
          >
            Events
          </Link>

          {/* Mobile Auth Buttons */}

          <div className="pt-3 border-t border-gray-200 flex flex-col gap-2">
            <Link to="https://dashboard.konn3ct.ng/join" className="w-full text-center py-2 text-lg text-blue-500">
              Join a Meeting Room
            </Link>

            <div className="pt-1 border-gray-200 flex gap-2">
              <button
                onClick={() => (window.location.href = "https://dashboard.konn3ct.ng/login")}
                className="w-1/3 py-2 text-sm border text-black border-[#1A4699] rounded-lg hover:text-blue-500"
              >
                Login
              </button>
              <button
                onClick={() => (window.location.href = "https://dashboard.konn3ct.ng/register")}
                className="w-2/3 py-2 text-sm text-white bg-[#1A4699] rounded-lg hover:bg-blue-500"
              >
                Sign up, It’s Free
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Konn3ctHeader;

